import { Slider as SliderPrimitive } from "@base-ui/react/slider";
import * as React from "react";

import { cn, cnState } from "@beweegbaas/ui/lib/utils";

interface SliderProps extends SliderPrimitive.Root.Props {
  /** Show tick marks at each step position */
  showTicks?: boolean;
  /** Labels to display below the slider, aligned with tick positions */
  labels?: React.ReactNode[];
  /** Callback when a label is clicked, receives the index */
  onLabelClick?: (index: number) => void;
  /** Tick/label indices that cannot be selected; value changes snap to the nearest enabled index */
  disabledIndices?: number[];
}

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  step = 1,
  showTicks = false,
  labels,
  onLabelClick,
  disabledIndices,
  onValueChange,
  children,
  ...props
}: SliderProps) {
  const _values = React.useMemo(
    () => (Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max]),
    [value, defaultValue, min, max],
  );

  const tickCount = Math.floor((max - min) / step) + 1;

  const currentValue = Array.isArray(value) ? value[0] : value;
  const currentIndex = currentValue !== undefined ? Math.round((currentValue - min) / step) : -1;

  const isDisabledIndex = (index: number) => disabledIndices?.includes(index) ?? false;

  const nearestEnabledIndex = (index: number): number | undefined => {
    if (!isDisabledIndex(index)) return index;
    for (let offset = 1; offset < tickCount; offset++) {
      if (index - offset >= 0 && !isDisabledIndex(index - offset)) return index - offset;
      if (index + offset < tickCount && !isDisabledIndex(index + offset)) return index + offset;
    }
    return undefined;
  };

  const handleValueChange: SliderPrimitive.Root.Props["onValueChange"] = (
    newValue,
    eventDetails,
  ) => {
    if (!onValueChange) return;
    const raw = Array.isArray(newValue) ? newValue[0] : newValue;
    if (raw === undefined) return;
    const snappedIndex = nearestEnabledIndex(Math.round((raw - min) / step));
    if (snappedIndex === undefined) return;
    const snappedValue = min + snappedIndex * step;
    onValueChange(Array.isArray(newValue) ? [snappedValue] : snappedValue, eventDetails);
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <SliderPrimitive.Root
        className={cn("data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full")}
        data-slot="slider"
        min={min}
        max={max}
        step={step}
        {...(value !== undefined ? { value } : { defaultValue })}
        thumbAlignment="edge"
        onValueChange={handleValueChange}
        {...props}
      >
        {children || (
          <SliderPrimitive.Control
            className="relative flex w-full touch-none items-center select-none
              data-disabled:opacity-50 data-[orientation=vertical]:h-full
              data-[orientation=vertical]:min-h-40
              data-[orientation=vertical]:w-auto
              data-[orientation=vertical]:flex-col"
          >
            <SliderPrimitive.Track
              data-slot="slider-track"
              className="data-disabled:bg-muted relative overflow-hidden
                rounded-full bg-blue-900/20 select-none
                data-[orientation=horizontal]:h-[6px]
                data-[orientation=horizontal]:w-full
                data-[orientation=vertical]:ml-[50%]
                data-[orientation=vertical]:h-full
                data-[orientation=vertical]:w-[6px]
                data-[orientation=vertical]:-translate-x-[50%] dark:bg-zinc-500
                data-disabled:dark:bg-zinc-800 forced-colors:bg-[ButtonBorder]
                data-disabled:forced-colors:bg-[GrayText]"
            >
              <SliderPrimitive.Indicator
                data-slot="slider-range"
                className="bg-primary select-none
                  data-[orientation=horizontal]:h-full
                  data-[orientation=vertical]:w-full"
              />
            </SliderPrimitive.Track>
            {showTicks && (
              <div
                data-slot="slider-ticks"
                className="pointer-events-none absolute inset-0 flex items-center
                  justify-between px-3"
              >
                {Array.from({ length: tickCount }, (_, index) => (
                  <div
                    key={index}
                    data-slot="slider-tick"
                    data-disabled={isDisabledIndex(index) ? true : undefined}
                    className="h-2.5 w-0.5 rounded-full bg-black/10
                      data-disabled:bg-black/5 dark:bg-white/15
                      data-disabled:dark:bg-white/10"
                  />
                ))}
              </div>
            )}
            {Array.from({ length: _values.length }, (_, index) => (
              <SliderPrimitive.Thumb
                data-slot="slider-thumb"
                key={index}
                className="block h-6 w-6 shrink-0 rounded-full bg-linear-to-br
                  from-blue-100 to-orange-100 shadow-sm ring-1 inset-shadow-sm
                  ring-black/60 inset-shadow-white/30 outline-offset-3
                  outline-blue-500/50 transition-[color,box-shadow] select-none
                  focus-visible:outline-2 disabled:pointer-events-none
                  disabled:opacity-50 data-disabled:border-gray-300
                  data-dragging:bg-gray-700 dark:outline-blue-500
                  data-disabled:dark:border-zinc-700
                  data-dragging:dark:bg-black/30
                  data-disabled:forced-colors:border-[GrayText]
                  data-dragging:forced-colors:bg-[ButtonBorder]"
              />
            ))}
          </SliderPrimitive.Control>
        )}
      </SliderPrimitive.Root>
      {labels && labels.length > 0 && (
        <div
          data-slot="slider-labels"
          className="text-muted-fg mt-1 flex justify-between px-3 text-xs"
        >
          {labels.map((label, index) =>
            onLabelClick ? (
              <button
                key={index}
                type="button"
                data-slot="slider-label"
                data-active={index === currentIndex ? true : undefined}
                disabled={isDisabledIndex(index)}
                onClick={() => onLabelClick(index)}
                className="hover:text-foreground data-active:text-foreground flex h-[1em] w-0
                  justify-center transition-colors
                  data-active:font-medium disabled:pointer-events-none
                  disabled:opacity-40"
              >
                {label}
              </button>
            ) : (
              <span
                key={index}
                data-slot="slider-label"
                data-active={index === currentIndex ? true : undefined}
                className="data-active:text-foreground flex w-0 justify-center
                  data-active:font-medium"
              >
                {label}
              </span>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function SliderTrack({
  className,
  children,
  ...props
}: SliderPrimitive.Track.Props & {
  children?: React.ReactNode | ((props: { state: { values: number[] } }) => React.ReactNode);
}) {
  return (
    <SliderPrimitive.Track
      data-slot="slider-track"
      className={cnState(
        `data-disabled:bg-muted relative overflow-hidden rounded-full
        bg-blue-900/20 select-none data-[orientation=horizontal]:h-[6px]
        data-[orientation=horizontal]:w-full
        data-[orientation=vertical]:ml-[50%] data-[orientation=vertical]:h-full
        data-[orientation=vertical]:w-[6px]
        data-[orientation=vertical]:-translate-x-[50%] dark:bg-zinc-500
        data-disabled:dark:bg-zinc-800`,
        className,
      )}
      {...props}
    >
      {typeof children === "function" ? children({ state: { values: [] } }) : children}
    </SliderPrimitive.Track>
  );
}

function SliderThumb({ className, index: _index, ...props }: SliderPrimitive.Thumb.Props) {
  return (
    <SliderPrimitive.Thumb
      data-slot="slider-thumb"
      className={cnState(
        `block h-6 w-6 shrink-0 rounded-full bg-linear-to-br from-blue-100
        to-orange-100 shadow-sm ring-1 inset-shadow-sm ring-black/60
        inset-shadow-white/30 outline-offset-3 outline-blue-500/50
        transition-[color,box-shadow] select-none focus-visible:outline-2
        disabled:pointer-events-none disabled:opacity-50`,
        className,
      )}
      {...props}
    />
  );
}

// Export SliderPrimitive for advanced use cases
export { Slider, SliderPrimitive, SliderThumb, SliderTrack };
